import { useState } from "react";
import { Button, Card, Empty, Icon, Popconfirm, Space, Tag, Text, useToast } from "@app/ui";
import { api } from "../api/client.js";
import { useErrorText } from "../errors/index.js";
import { useT, useFormatDateTime } from "../i18n.js";
import { deviceFromUserAgent } from "./identity.js";
import type { SessionInfo } from "../types.js";

/**
 * Signed-in devices. A table of opaque session ids told the user nothing they
 * could act on, so each row now leads with the device it most likely is (read
 * off the user agent) and the facts that identify it: when it signed in, from
 * which IP, and whether it cleared MFA.
 */

export function DevicesTab({ sessions, reload }: { sessions: SessionInfo[]; reload: () => Promise<void> }) {
  const t = useT();
  const toast = useToast();
  const errorText = useErrorText();
  const fmtDate = useFormatDateTime();

  const [busy, setBusy] = useState(false);

  const others = sessions.filter((s) => !s.current);

  const revoke = async (sessionId: string) => {
    setBusy(true);
    try {
      await api<void>(`/auth/sessions/${sessionId}`, { method: "DELETE" });
      toast.success(t("profile.revoked"));
      await reload();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  // No bulk endpoint exists — revoke one by one and report what actually landed,
  // so a partial failure is not dressed up as a clean sweep.
  const revokeOthers = async () => {
    setBusy(true);
    let done = 0;
    try {
      for (const s of others) {
        await api<void>(`/auth/sessions/${s.sessionId}`, { method: "DELETE" });
        done++;
      }
      toast.success(t("profile.revokedOthers", { count: done }));
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
      await reload();
    }
  };

  return (
    <Card
      title={`${t("profile.devices")} (${sessions.length})`}
      extra={
        others.length > 0 ? (
          <Popconfirm
            title={t("profile.revokeOthersConfirm")}
            description={t("profile.revokeOthersDescription")}
            okText={t("profile.revokeOthers")}
            cancelText={t("common.cancel")}
            okType="danger"
            placement="left"
            onConfirm={() => void revokeOthers()}
          >
            <Button size="sm" variant="danger" loading={busy} icon={<Icon name="log-out" size={14} />}>
              {t("profile.revokeOthers")}
            </Button>
          </Popconfirm>
        ) : undefined
      }
    >
      <Space direction="vertical" size="md" className="w-full">
        <Text small secondary>
          {t("profile.devicesHint")}
        </Text>

        {sessions.length === 0 ? (
          <Empty description={t("dashboard.sessionsEmpty")} />
        ) : (
          <div className="pf-devices">
            {sessions.map((s) => {
              const device = deviceFromUserAgent(s.userAgent, t("profile.deviceUnknown"));
              return (
                <div key={s.sessionId} className={`pf-device${s.current ? " is-current" : ""}`}>
                  <span className="pf-device__icon">
                    <Icon name={device.icon} size={18} />
                  </span>
                  <div className="pf-device__body">
                    <div className="pf-device__title">
                      {device.label}
                      {s.current && <Tag color="primary">{t("dashboard.thisDevice")}</Tag>}
                      {s.mfaSatisfied && <Tag color="success">{t("profile.mfaVerified")}</Tag>}
                    </div>
                    <div className="pf-device__meta">
                      <span>
                        <Icon name="clock" size={12} />
                        {`${t("dashboard.sessionStarted")}: ${fmtDate(s.createdAt)}`}
                      </span>
                      <span>
                        <Icon name="map-pin" size={12} />
                        {`${t("common.ip")}: ${s.ip ?? "—"}`}
                      </span>
                      <span>
                        <Icon name="calendar" size={12} />
                        {`${t("profile.sessionExpires")}: ${fmtDate(s.expiresAt)}`}
                      </span>
                    </div>
                  </div>
                  {!s.current && (
                    <Popconfirm
                      title={t("profile.revokeConfirm")}
                      description={t("profile.revokeDescription")}
                      okText={t("dashboard.revoke")}
                      cancelText={t("common.cancel")}
                      okType="danger"
                      placement="left"
                      onConfirm={() => void revoke(s.sessionId)}
                    >
                      <Button size="sm" variant="subtle" disabled={busy}>
                        {t("dashboard.revoke")}
                      </Button>
                    </Popconfirm>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Space>
    </Card>
  );
}
